import { useEffect } from "react"
import { ssoApi } from "../../services/api.service";
import { setToken, setUserDetails } from "../../services/user.service";
import { getCognitoAdminGroups } from "../../services/config.service";

export const CallBack = ()=> {
    const getToken = async (id:string)=> {
        if(!id) {
            document.location.href = "/login";
        } else {
            const result = await ssoApi.get(`/auth/getToken?id=${id}`);
            setToken(result?.data?.data?.accessToken);
            setUserDetails(result?.data?.data);
            const redirectUrl = localStorage.getItem('REDIRECT_URL');
            if (redirectUrl) {
                localStorage.removeItem('REDIRECT_URL');
                document.location.href = redirectUrl;
            } else {
                document.location.href = "/";
            }
        }
    }

    const handleCognitoCallbackWithBothTokens = async (accessToken: string, idToken: string) => {
        // Store access_token for API authentication
        setToken(accessToken);

        // Decode id_token to extract user details
        try {
            const base64Url = idToken.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            const payload = JSON.parse(jsonPayload);

            // Log all JWT attributes
            // console.log('JWT Token Payload (all attributes):', payload);

            // Check both cognito:groups and custom:groups for roles
            const cognitoGroups = payload['cognito:groups'] || [];
            const customGroups = payload['custom:groups'] ? payload['custom:groups'].split(',') : [];
            const allGroups = [...cognitoGroups, ...customGroups];


            // Fetch configured admin groups from config API
            const configuredAdminGroups = await getCognitoAdminGroups();

            // Check cognito:groups against configured admin groups
            const cognitoAdminMatch = cognitoGroups.filter((userGroup: string) =>
                configuredAdminGroups.includes(userGroup));

            // Check if user belongs to any configured admin group (exact match) from allGroups
            const isAdmin = configuredAdminGroups.length > 0 &&
                allGroups.some((userGroup: string) => configuredAdminGroups.includes(userGroup));


            // Map Cognito groups to app roles (ROLE_ADMIN, SUPER_ADMIN)
            // The app expects 'ROLE_ADMIN' or 'SUPER_ADMIN' for admin features
            const appRoles = isAdmin ? ['ROLE_ADMIN', ...allGroups] : allGroups;

            // Set user details from ID token payload (has email, cognito:username, etc.)
            const userDetails = {
                username: payload['cognito:username'] || payload.email || payload.sub,
                email: payload.email,
                roles: appRoles,
                id: payload.sub
            };
            setUserDetails(userDetails);
        } catch (error) {
            console.error('Error decoding ID token:', error);
            alert('Error decoding ID token. Check console for details.');
            document.location.href = "/login";
            return;
        }

        const redirectUrl = localStorage.getItem('REDIRECT_URL');

        if (redirectUrl) {
            localStorage.removeItem('REDIRECT_URL');
            console.log('Redirecting to:', redirectUrl);
            document.location.href = redirectUrl;
        } else {
            console.log('Redirecting to home');
            document.location.href = "/";
        }
    }

    const handleCognitoCallback = async (token: string) => {
        // For Cognito, the JWT is returned directly in the URL
        // We need to verify and decode it, then extract user details

        if (token) {
            setToken(token);

            // Decode JWT to extract user details (without verification on client side)
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const payload = JSON.parse(jsonPayload);


                // Check both cognito:groups and custom:groups for roles
                const cognitoGroups = payload['cognito:groups'] || [];
                const customGroups = payload['custom:groups'] ? payload['custom:groups'].split(',') : [];
                const allGroups = [...cognitoGroups, ...customGroups];

                // Fetch configured admin groups from config API
                const configuredAdminGroups = await getCognitoAdminGroups();

                // Check cognito:groups against configured admin groups
                const cognitoAdminMatch = cognitoGroups.filter((userGroup: string) =>
                    configuredAdminGroups.includes(userGroup));

                // Check if user belongs to any configured admin group (exact match) from allGroups
                const isAdmin = configuredAdminGroups.length > 0 &&
                    allGroups.some((userGroup: string) => configuredAdminGroups.includes(userGroup));

                // Map Cognito groups to app roles (ROLE_ADMIN, SUPER_ADMIN)
                // The app expects 'ROLE_ADMIN' or 'SUPER_ADMIN' for admin features
                const appRoles = isAdmin ? ['ROLE_ADMIN', ...allGroups] : allGroups;

                // Set user details from JWT payload
                const userDetails = {
                    username: payload['cognito:username'] || payload.username || payload.email || payload.sub,
                    email: payload.email,
                    roles: appRoles,
                    id: payload.sub
                };
                setUserDetails(userDetails);
            } catch (error) {
                console.error('Error decoding JWT:', error);
                alert('Error decoding JWT token. Check console for details.');
                document.location.href = "/login";
                return;
            }

            const redirectUrl = localStorage.getItem('REDIRECT_URL');

            if (redirectUrl) {
                localStorage.removeItem('REDIRECT_URL');
                console.log('Redirecting to:', redirectUrl);
                document.location.href = redirectUrl;
            } else {
                console.log('Redirecting to home');
                document.location.href = "/";
            }
        } else {
            console.log('No token provided, redirecting to login');
            document.location.href = "/login";
        }
    }

    useEffect(()=> {
        console.log('=== Callback Page Loaded ===');
        console.log('Full URL:', window.location.href);
        console.log('Search params:', window.location.search);

        // Check URL query parameters (for SSO and authorization code flow)
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get('id');
        const code = urlParams.get('code'); // For authorization code flow
        let token = urlParams.get('token') || urlParams.get('access_token') || urlParams.get('id_token');


        // Check URL hash fragment (for Cognito implicit flow)
        if (!token && window.location.hash) {
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            // Use id_token preferably as it contains user info (email, etc.)
            // Store access_token for API calls
            const accessToken = hashParams.get('access_token');
            const idToken = hashParams.get('id_token');

            if (accessToken && idToken) {
                // We have both tokens - use access_token for API, id_token for user info
                handleCognitoCallbackWithBothTokens(accessToken, idToken);
                return; // Exit early
            } else {
                // Fallback to single token
                token = accessToken || idToken;
            }
        }

        if (token) {
            // Cognito flow - JWT returned directly
            console.log('Using Cognito flow with token');
            handleCognitoCallback(token);
        } else if (code) {
            // Authorization code flow - would need to exchange code for token
            console.log('Received authorization code:', code);
            // TODO: Implement code exchange if using authorization code flow
            alert('Authorization code flow not yet implemented. Please use implicit flow (response_type=token)');
            document.location.href = "/login";
        } else if (id) {
            // SSO flow - exchange ID for token
            console.log('Using SSO flow with id');
            getToken(id);
        } else {
            // No valid parameters, redirect to login
            console.log('No valid auth parameters found, redirecting to login');
            document.location.href = "/login";
        }
    },[])

    return null;
}
